import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe2, ScrollText, Layers, Gem } from "lucide-react";
import api from "@/lib/api";
import { PyramidCompactCard } from "@/components/PyramidCompactCard";
import { AREAS } from "@/lib/phases";

const HERO_IMG =
  "https://images.pexels.com/photos/36505459/pexels-photo-36505459.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

export default function Home() {
  const [pyramids, setPyramids] = useState([]);
  const [stats, setStats] = useState({ total: 0, countries: 0, by_phase: {} });

  useEffect(() => {
    api.get("/pyramids", { params: { limit: 200 } }).then((r) => {
      const curated = r.data.filter((p) => p.source === "curated");
      const shuffled = [...curated].sort(() => Math.random() - 0.5).slice(0, 12);
      setPyramids(shuffled);
    }).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Ancient pyramid" className="h-full w-full object-cover" style={{ objectPosition: "50% 32%" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-10 sm:py-16">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-accent mb-3 fade-up">
            Structures · Religion · Language
          </p>
          <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] max-w-3xl text-foreground fade-up" style={{ animationDelay: "80ms" }}>
            Things we<br />cannot fully<br /><span className="text-accent italic">explain.</span>
          </h1>
          <p className="mt-4 max-w-xl text-base sm:text-lg text-muted-foreground leading-relaxed fade-up" style={{ animationDelay: "160ms" }}>
            An investigative archive of the world's enduring enigmas — the impossible structures,
            the lost religions and the undeciphered languages that still resist explanation.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 fade-up" style={{ animationDelay: "240ms" }}>
            <Link to="/map" data-testid="hero-map-btn" className="group flex items-center gap-2 bg-accent text-accent-foreground font-mono text-xs uppercase tracking-widest px-6 py-3.5 hover:bg-accent/90 transition-colors">
              <Globe2 className="h-4 w-4" /> Explore the Map
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/archive" data-testid="hero-archive-btn" className="flex items-center gap-2 border border-foreground/30 text-foreground font-mono text-xs uppercase tracking-widest px-6 py-3.5 hover:bg-foreground/5 transition-colors">
              Browse Archive
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { icon: Layers, n: stats.total?.toLocaleString?.() || 0, l: "Curated Entries", to: "/archive", testid: "stat-curated" },
            { icon: Gem, n: (stats.to_explore || 0).toLocaleString(), l: "Still to Explore", to: "/archive?full=1", testid: "stat-still-to-explore" },
            { icon: Globe2, n: stats.countries_all || stats.countries || 0, l: "Countries" },
            { icon: ScrollText, n: Object.keys(stats.by_area_all || stats.by_area || {}).length || 3, l: "Areas of Study" },
          ].map((s, i) => {
            const inner = (
              <>
                <s.icon className="h-5 w-5 text-accent mx-auto sm:mx-0 mb-1.5" />
                <div className="font-serif text-3xl sm:text-4xl font-bold text-foreground">{s.n}</div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mt-0.5">{s.l}</div>
              </>
            );
            return s.to ? (
              <Link key={i} to={s.to} data-testid={s.testid} className="text-center sm:text-left group hover:opacity-90 transition-opacity">
                {inner}
                <span className="mt-0.5 hidden sm:inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-accent opacity-0 group-hover:opacity-100 transition-opacity">Explore <ArrowRight className="h-3 w-3" /></span>
              </Link>
            ) : (
              <div key={i} className="text-center sm:text-left">{inner}</div>
            );
          })}
        </div>
      </section>

      {/* Areas */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 pt-8 sm:pt-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {AREAS.map((a) => {
            const curated = stats.by_area?.[a.key] || 0;
            const all = stats.by_area_all?.[a.key] || curated;
            const more = Math.max(all - curated, 0);
            return (
              <Link
                key={a.key}
                to={`/archive?area=${a.key}`}
                data-testid={`home-area-${a.key}`}
                className="group relative overflow-hidden border border-border bg-card p-5 hover:-translate-y-1 transition-transform"
              >
                <span className="absolute top-0 left-0 h-full w-1" style={{ background: a.color }} />
                <p className="font-mono text-xs uppercase tracking-[0.25em]" style={{ color: a.color }}>Area</p>
                <h3 className="mt-1.5 font-serif text-2xl font-bold text-foreground">{a.label}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{a.blurb}</p>
                <p className="mt-3 font-mono text-sm text-foreground">
                  {curated.toLocaleString()} curated
                  {more > 0 && <span className="text-muted-foreground"> · {more.toLocaleString()} to explore</span>}
                </p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-8 sm:py-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-2">Selected Entries</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground">The Catalog</h2>
          </div>
          <Link to="/archive" className="hidden sm:flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-accent transition-colors">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {pyramids.map((p, i) => {
            const vis = i < 4 ? "" : i < 6 ? "hidden sm:block" : i < 8 ? "hidden md:block" : "hidden lg:block";
            return (
              <div key={p.slug} className={vis}>
                <PyramidCompactCard p={p} index={i} />
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
